// Adaptive Module 2: Seating Guests (Abstract Reasoning)
// Replaces Module2_1 through Module2_4 with adaptive difficulty

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Draggable, Droppable } from 'react-drag-and-drop';
import { UserOutlined } from '@ant-design/icons';
import { Modal, Avatar } from 'antd';
import { FaChevronRight } from 'react-icons/fa';
import { useRecoilState } from 'recoil';
import { sessionTimingAtom } from '../../../../state/assessment';
import AssessmentWrapper from '../../AssessmentWrapper';
import { MODULE_2_INSTANCES, MODULE_2_SOLUTIONS } from '../../../../data/moduleInstances';
import ManCard from '../ManCard';
import Chair from '../../../../resources/imgs/assessment/chair.svg';
import Placeholder from '../../../../resources/imgs/assessment/Placeholder.png';
import HelpBtn from '../../../../resources/imgs/assessment/helpBtn.svg';
import Close from '../../../../resources/imgs/modal/close.svg';

// Character images for the seating arrangement
import Manuel from '../../../../resources/imgs/assessment/characters/Manuel.png';
import Karl from '../../../../resources/imgs/assessment/characters/Karl.png';
import Pamela from '../../../../resources/imgs/assessment/characters/Pamela.png';
import Trish from '../../../../resources/imgs/assessment/characters/Trish.png';
import Joey from '../../../../resources/imgs/assessment/characters/joey1.png';
import Reina from '../../../../resources/imgs/assessment/characters/reina1.png';

const SeatingGuestsContent = ({ instance, onSubmit, instanceNumber, moduleId, dynamicCardData }) => {
  const [open, setOpen] = useState(false);
  const [imgIndex, setImageIndex] = useState([]);
  const [flag, setFlag] = useState(0);
  const [cardData, setCardData] = useState([]);
  const [dynamicToOriginalMapping, setDynamicToOriginalMapping] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const [sessionTiming, setSessionTiming] = useRecoilState(sessionTimingAtom);
  const [instanceStartTime, setInstanceStartTime] = useState(null);

  // Mapping dish names to indices (memoized to prevent unnecessary re-renders)
  const dishNameToIndex = useMemo(() => ({
    "Fish": 0, "Chicken": 1, "Pasta": 2, 
    "Steak": 3, "Vegetarian": 4, "Lamb": 5
  }), []);
  
  // Convert MODULE_2_INSTANCES guest preferences to ManCard format
  const convertGuestPreferences = useCallback((guestName, preferences) => {
    const dishArray = [
      dishNameToIndex[preferences.preferred],
      dishNameToIndex[preferences.second],
      dishNameToIndex[preferences.detests]
    ];
    return dishArray;
  }, [dishNameToIndex]);
  
  // Update card data with instance preferences (only when instance ID changes)
  useEffect(() => {
    if (!instance || !instance.guests || !dynamicCardData || dynamicCardData.length === 0) return;
    
    // Get the original guest names from the instance
    const originalGuestNames = Object.keys(instance.guests);
    
    // Create a mapping: we'll assign preferences in order
    // This maintains consistency - first dynamic character gets first original guest's preferences, etc.
    const mapping = {};
    
    // Map the current card data (dynamic characters) to the instance preferences
    const newCardData = dynamicCardData.slice(0, 6).map((card, index) => {
      // Map each dynamic character to the preferences of the corresponding original guest by position
      if (index < originalGuestNames.length) {
        const originalGuestName = originalGuestNames[index];
        const guestPreferences = instance.guests[originalGuestName];
        
        // Store the mapping for validation later
        mapping[card.name] = originalGuestName;
        
        if (guestPreferences) {
          const newDishArray = convertGuestPreferences(card.name, guestPreferences);
          return {
            img: card.img || card.otherImg,
            name: card.name,
            dish: newDishArray,
            visible: true,
            originalName: originalGuestName // Store the original name for reference
          };
        }
      }
      
      return {
        img: card.img || card.otherImg,
        name: card.name,
        dish: card.dish || [0, 1, 2],
        visible: true
      };
    });
    
    console.log('🗺️ Module 2 Character Mapping:');
    console.log('  Dynamic characters:', dynamicCardData.slice(0, 6).map(c => c.name));
    console.log('  Original characters:', originalGuestNames);
    console.log('  Mapping:', mapping);
    console.log('  New card data:', newCardData.map(c => `${c.name}(${c.originalName}): [${c.dish}]`));
    
    setCardData(newCardData);
    setDynamicToOriginalMapping(mapping);
    
    // Reset seating state when instance changes
    setImageIndex([]);
    setFlag(0);
    
    // Initialize timer based on difficulty
    const timerDuration = instance.difficulty <= 5 ? 75 : 60;
    setTimeRemaining(timerDuration);
    setTimerStarted(true);

    // Initialize session start time if not already set
    const now = Date.now();
    if (!sessionTiming.sessionStartTime) {
      setSessionTiming(prev => ({
        ...prev,
        sessionStartTime: now
      }));
    }

    // Record instance start time
    setInstanceStartTime(now);
    console.log(`⏱️ Module 2 Instance ${instanceNumber} started at ${now - (sessionTiming.sessionStartTime || now)}ms from session start`);
  }, [instance?.instanceId, convertGuestPreferences, dynamicCardData, sessionTiming.sessionStartTime, instanceNumber]); // Include dynamicCardData to prevent stale closures

  // Helper function to get the dish a guest is having based on seating preferences
  const getDishFromPreferences = useCallback((guestName, guests) => {
    // Map dynamic name back to original name
    const originalName = dynamicToOriginalMapping[guestName] || guestName;
    const guest = guests[originalName];
    if (!guest) return null;
    // For now, assume they get their preferred dish
    // In a full implementation, this would be determined by the seating logic
    return guest.preferred;
  }, [dynamicToOriginalMapping]);
  
  // Helper function to check if two arrays are equal
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };
  
  // Helper function to check if two arrays are equal considering rotations (for circular table)
  const arraysEqualWithRotation = (userArrangement, validSolution) => {
    if (userArrangement.length !== validSolution.length) return false;
    
    const n = userArrangement.length;
    
    // Try each possible rotation of the user arrangement
    for (let rotation = 0; rotation < n; rotation++) {
      let matches = true;
      for (let i = 0; i < n; i++) {
        const userIndex = (i + rotation) % n;
        if (userArrangement[userIndex] !== validSolution[i]) {
          matches = false;
          break;
        }
      }
      if (matches) {
        console.log(`✅ Match found with rotation ${rotation}`);
        return true;
      }
    }
    
    // Also try reverse direction (clockwise vs counter-clockwise)
    for (let rotation = 0; rotation < n; rotation++) {
      let matches = true;
      for (let i = 0; i < n; i++) {
        const userIndex = (rotation - i + n) % n;
        if (userArrangement[userIndex] !== validSolution[i]) {
          matches = false;
          break;
        }
      }
      if (matches) {
        console.log(`✅ Match found with reverse rotation ${rotation}`);
        return true;
      }
    }
    
    return false;
  };
  
  // Fallback constraint-based validation (original logic)
  const validateSeatingArrangementConstraints = useCallback((arrangement, guests) => {
    // Create circular arrangement for easier neighbor checking
    // Current visual positions: 0=top, 1=right-mid-low, 2=bottom, 3=left-mid, 4=right-mid, 5=left-bottom
    // Desired circular flow: 0(top) → 4(right-mid) → 1(right-mid-low) → 2(bottom) → 5(left-bottom) → 3(left-mid) → 0
    const getNeighbors = (index) => {
      const visualCircularNeighbors = {
        0: [3, 4], // top center: connects to left-mid and right-mid  
        4: [0, 1], // right-mid: connects to top and right-mid-low
        1: [4, 2], // right-mid-low: connects to right-mid and bottom
        2: [1, 5], // bottom center: connects to right-mid-low and left-bottom
        5: [2, 3], // left-bottom: connects to bottom and left-mid
        3: [5, 0]  // left-mid: connects to left-bottom and top
      };
      return visualCircularNeighbors[index] || [];
    };
    
    // Check constraints for each guest
    for (let i = 0; i < arrangement.length; i++) {
      const guest = arrangement[i];
      const guestPrefs = guests[guest.personName];
      if (!guestPrefs) continue;
      
      const neighborIndices = getNeighbors(i);
      const neighbors = neighborIndices.map(idx => arrangement[idx]);
      
      // Rule 1: Must sit next to a guest having their second favorite dish
      let hasSecondFavoriteNeighbor = false;
      for (const neighbor of neighbors) {
        if (neighbor.dish === guestPrefs.second) {
          hasSecondFavoriteNeighbor = true;
          break;
        }
      }
      if (!hasSecondFavoriteNeighbor) {
        console.log(`${guest.personName} doesn't have neighbor with second favorite dish (${guestPrefs.second})`);
        return false;
      }
      
      // Rule 2: Cannot sit next to guest eating detested dish  
      for (const neighbor of neighbors) {
        if (neighbor.dish === guestPrefs.detests) {
          console.log(`${guest.personName} is sitting next to someone eating detested dish (${guestPrefs.detests})`);
          return false;
        }
      }
    }
    
    return true;
  }, []);
  
  // Validate seating arrangement against known solutions (rotation-aware)
  const validateSeatingArrangement = useCallback((arrangement, guests) => {
    if (arrangement.length !== 6) return false;
    
    // Use the actual instance ID from the instance data, not the attempt number
    const instanceKey = instance?.instanceId?.toString() || instanceNumber?.toString();
    const validSolutions = MODULE_2_SOLUTIONS[instanceKey];
    
    console.log(`🔍 Validating arrangement for instance ${instanceKey} (instanceNumber=${instanceNumber}, instance.instanceId=${instance?.instanceId})`);
    
    if (!validSolutions) {
      console.warn(`⚠️ No solutions found for instance ${instanceKey}, falling back to constraint checking`);
      return validateSeatingArrangementConstraints(arrangement, guests);
    }
    
    // Map visual seat positions to logical circular seat numbers
    const visualToLogicalSeat = {
      0: 0, // top center stays the same
      4: 1, // right-mid becomes seat 1  
      1: 2, // right-mid-low becomes seat 2
      2: 3, // bottom center becomes seat 3
      5: 4, // left-bottom becomes seat 4
      3: 5  // left-mid becomes seat 5
    };
    
    // Convert arrangement using the new logical seat numbers
    const logicalArrangement = arrangement.map(guest => ({
      ...guest,
      logicalSeatIndex: visualToLogicalSeat[guest.seatIndex]
    }));
    
    // Sort by logical seat index to get correct circular order
    const sortedArrangement = [...logicalArrangement].sort((a, b) => a.logicalSeatIndex - b.logicalSeatIndex);
    const arrangementNames = sortedArrangement.map(guest => guest.personName);
    
    console.log(`🔍 User arrangement: [${arrangementNames.join(', ')}]`);
    
    // Use the stored mapping from dynamic names to original names
    const nameToOriginalMapping = dynamicToOriginalMapping;
    
    console.log(`🔍 Name mapping (dynamic -> original):`, nameToOriginalMapping);
    
    // Convert the user's arrangement from dynamic names to original names
    const arrangementWithOriginalNames = arrangementNames.map(dynamicName => {
      const originalName = nameToOriginalMapping[dynamicName];
      if (!originalName) {
        console.warn(`⚠️ No mapping found for dynamic name: ${dynamicName}`);
      }
      return originalName || dynamicName;
    });
    
    console.log(`🔍 User arrangement (with original names): [${arrangementWithOriginalNames.join(', ')}]`);
    console.log(`🔍 Valid solutions for instance ${instanceKey}:`, validSolutions);
    
    // Check if the arrangement matches any of the valid solutions (considering rotations)
    for (const validSolution of validSolutions) {
      if (arraysEqualWithRotation(arrangementWithOriginalNames, validSolution)) {
        console.log('✅ Seating arrangement matches valid solution (with rotation)!');
        return true;
      }
    }
    
    console.log(`❌ Seating arrangement [${arrangementWithOriginalNames.join(', ')}] doesn't match any valid solutions`);
    return false;
  }, [dynamicToOriginalMapping, instance, instanceNumber, validateSeatingArrangementConstraints]);

  // Timer countdown effect
  useEffect(() => {
    if (!timerStarted || timeRemaining === null || timeRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timerStarted]);
  
  // Handle timer expiration
  useEffect(() => {
    if (timeRemaining === 0) {
      handleTimeUp();
    }
  }, [timeRemaining]);
  
  // Check if arrangement is correct without submitting
  const isArrangementCorrect = useCallback(() => {
    if (!flag || imgIndex.filter(i => i !== undefined).length !== 6) return false;
    
    // Create seating arrangement from current state
    const seatingArrangement = imgIndex.map((personIndex, seatIndex) => {
      if (personIndex !== undefined) {
        return {
          personName: cardData[personIndex].name,
          seatIndex: seatIndex,
          dish: getDishFromPreferences(cardData[personIndex].name, instance.guests)
        };
      }
      return null;
    }).filter(seat => seat !== null);
    
    return validateSeatingArrangement(seatingArrangement, instance.guests);
  }, [flag, imgIndex, cardData, instance, dynamicToOriginalMapping]);
  
  // Handle when timer expires
  const handleTimeUp = useCallback(() => {
    console.log('⏰ Time is up! Checking arrangement...');
    
    if (flag === 1) {
      // All guests are seated
      const isCorrect = isArrangementCorrect();
      
      if (isCorrect) {
        console.log('✅ All guests correctly placed - auto-submitting!');
        checkSolution();
      } else {
        console.log('❌ Time expired - arrangement incomplete or incorrect');

        // Record trial timing data for timeout
        const now = Date.now();
        const sessionStart = sessionTiming.sessionStartTime || now;
        const trialStart = instanceStartTime || now;

        const trialData = {
          module_id: '2',
          instance_id: instance?.instanceId?.toString() || instanceNumber?.toString(),
          timestamp_start_ms: trialStart - sessionStart,
          timestamp_response_ms: now - sessionStart,
          response_latency_ms: now - trialStart,
          is_correct: false,
          error_type: 'accuracy',
          difficulty_level: instance.difficulty,
          trial_number: instanceNumber
        };

        setSessionTiming(prev => ({
          ...prev,
          trialData: [...prev.trialData, trialData]
        }));

        // Submit as incorrect due to timeout
        onSubmit(false, {
          seatingArrangement: [],
          guestPreferences: instance.guests,
          difficulty: instance.difficulty,
          timedOut: true,
          trialData
        });
      }
    } else {
      console.log('❌ Time expired - not all guests seated');

      // Record trial timing data for timeout
      const now = Date.now();
      const sessionStart = sessionTiming.sessionStartTime || now;
      const trialStart = instanceStartTime || now;

      const trialData = {
        module_id: '2',
        instance_id: instance?.instanceId?.toString() || instanceNumber?.toString(),
        timestamp_start_ms: trialStart - sessionStart,
        timestamp_response_ms: now - sessionStart,
        response_latency_ms: now - trialStart,
        is_correct: false,
        error_type: 'accuracy',
        difficulty_level: instance.difficulty,
        trial_number: instanceNumber
      };

      setSessionTiming(prev => ({
        ...prev,
        trialData: [...prev.trialData, trialData]
      }));

      // Submit as incorrect due to timeout
      onSubmit(false, {
        seatingArrangement: [],
        guestPreferences: instance.guests,
        difficulty: instance.difficulty,
        timedOut: true,
        trialData
      });
    }
  }, [flag, isArrangementCorrect, instance, sessionTiming, instanceStartTime, instanceNumber, setSessionTiming]);

  const handleDrop = useCallback((val, idx) => {
    let data = [...cardData];
    const removeIdx = imgIndex.indexOf(val);
    const newImgIndex = [...imgIndex];
    
    // If the item was previously placed somewhere else, clear that position
    if (removeIdx !== -1) {
      newImgIndex[removeIdx] = undefined;
    }
    
    // Check if the target slot is already occupied
    const previousItemAtSlot = newImgIndex[idx];
    
    // If there was a previous item in this slot, make it visible again
    if (previousItemAtSlot !== undefined) {
      data[previousItemAtSlot].visible = true;
    }
    
    // Hide the item being placed and place it in the target slot
    data[val].visible = false;
    newImgIndex[idx] = val;
    setImageIndex(newImgIndex);

    // Check if all positions are filled
    let flag = 1;
    for (let i = 0; i < 6; i++) {
      if (newImgIndex[i] === undefined) {
        flag = 0;
        break;
      }
    }

    setFlag(flag);
    setCardData(data);
  }, [cardData, imgIndex]);

  const resetSeat = useCallback(() => {
    setCardData(prev => prev.map(item => ({ ...item, visible: true })));
    setImageIndex([]);
    setFlag(0);
  }, []);

  const showModal = useCallback(() => setOpen(true), []);
  const hideModal = useCallback(() => setOpen(false), []);

  // Early return after all hooks are defined
  if (!instance || !instance.guests) {
    return <div>Loading seating arrangement...</div>;
  }

  const checkSolution = () => {
    console.log('🚀 checkSolution called!');
    console.log('🚀 flag:', flag);
    console.log('🚀 imgIndex:', imgIndex);
    console.log('🚀 imgIndex breakdown:');
    imgIndex.forEach((personIndex, seatIndex) => {
      if (personIndex !== undefined) {
        console.log(`  Seat ${seatIndex}: ${cardData[personIndex].name}`);
      } else {
        console.log(`  Seat ${seatIndex}: empty`);
      }
    });
    console.log('🚀 cardData:', cardData.map(c => c.name));
    console.log('🚀 instanceNumber:', instanceNumber);
    
    if (flag !== 1) {
      console.log('❌ Not all guests seated, flag =', flag);
      alert("Please seat all guests before submitting!");
      return;
    }
    
    // Create seating arrangement from current state
    const seatingArrangement = imgIndex.map((personIndex, seatIndex) => {
      if (personIndex !== undefined) {
        return {
          personName: cardData[personIndex].name,
          seatIndex: seatIndex,
          dish: getDishFromPreferences(cardData[personIndex].name, instance.guests)
        };
      }
      return null;
    }).filter(seat => seat !== null);
    
    console.log('🚀 Created seating arrangement:', seatingArrangement);
    
    // Check if arrangement satisfies constraints
    const isCorrect = validateSeatingArrangement(seatingArrangement, instance.guests);

    console.log('🚀 Seating arrangement:', seatingArrangement);
    console.log('🚀 Is correct:', isCorrect);

    // Record trial timing data
    const now = Date.now();
    const responseTime = now;
    const sessionStart = sessionTiming.sessionStartTime || now;
    const trialStart = instanceStartTime || now;

    const trialData = {
      module_id: '2',
      instance_id: instance?.instanceId?.toString() || instanceNumber?.toString(),
      timestamp_start_ms: trialStart - sessionStart,
      timestamp_response_ms: responseTime - sessionStart,
      response_latency_ms: responseTime - trialStart,
      is_correct: isCorrect,
      error_type: isCorrect ? 'none' : 'accuracy', // Could be 'rule' if constraint violation
      difficulty_level: instance.difficulty,
      trial_number: instanceNumber
    };

    console.log('⏱️ Module 2 trial data:', trialData);

    // Add to session timing atom
    setSessionTiming(prev => ({
      ...prev,
      trialData: [...prev.trialData, trialData]
    }));

    onSubmit(isCorrect, {
      seatingArrangement,
      guestPreferences: instance.guests,
      difficulty: instance.difficulty,
      trialData // Include trial data in submission
    });
  };

  return (
    <div className="module1-main">
      <div className="module1-wrap">
        <div className="module1-content-wrap">
          <div className="module1-logo">Module 2 - {instance.title}</div>
          <div className="module1-body">
            <div className="module1-title">
              Drag guests to seats (Difficulty: {instance.difficulty}/10) - Instance {instanceNumber}/5
            </div>
            {timeRemaining !== null && (
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#ff4444',
                margin: '10px 0',
                textAlign: 'center'
              }}>
                Time Remaining: {timeRemaining}s
              </div>
            )}
            <div className="module1-content1">
              Drag and drop the guests to spots at the table to ensure
              everyone is seated correctly according to their preferences.
            </div>
          </div>
        </div>
      </div>

      <div className="module1-card">
        {cardData.map((item, index) => (
          <ManCard 
            key={`${instance?.instanceId}-${item.name}-${index}`} 
            img={item.img} 
            name={item.name} 
            dish={item.dish} 
          />
        ))}
      </div>

      <div className="assessment-reset-btn" onClick={resetSeat}>
        Reset
      </div>

      <div className="module1-seat">
        <div className="module1-man-group">
          {cardData.map((item, index) => {
            if (item.visible) {
              return (
                <Draggable key={index} type="item" data={index}>
                  <img src={item.img} alt={item.name} />
                </Draggable>
              );
            } else {
              return (
                <Avatar 
                  key={index}
                  className='placeholder-avatar' 
                  style={{ width: "130px", height: "130px", opacity: 0.7 }} 
                  icon={<UserOutlined style={{ fontSize: '50px', opacity: "0.5" }} />} 
                />
              );
            }
          })}
        </div>

        <div className="module1-seat-group">
          {[0, 1, 2, 3, 4, 5].map(seatIndex => (
            <Droppable
              key={seatIndex}
              className={`module1-circle box${seatIndex + 1}`}
              types={["item"]}
              onDrop={(data) => handleDrop(data.item, seatIndex)}
            >
              <img
                className="module1-circle-img"
                src={imgIndex[seatIndex] !== undefined ? cardData[imgIndex[seatIndex]].img : Placeholder}
                alt={`Seat ${seatIndex + 1}`}
              />
            </Droppable>
          ))}
          <img className='module1-chair' src={Chair} alt="Chair" />
        </div>
      </div>

      <div className='next-btn-group'>
        <div className='help-btn-group'>
          <div className='help-btn-effect1'></div>
          <div className='help-btn-effect2'></div>
          <img src={HelpBtn} onClick={showModal} alt="Help" />
          <div className='help-content'>Do you need help?</div>
        </div>

        {flag === 1 && (
          <div className="assessment-next-btn" onClick={checkSolution}>
            Submit Arrangement{" "}
            <div style={{ paddingTop: "5px", display: "inline-block" }}>
              <FaChevronRight color="white" size={16} />
            </div>
          </div>
        )}
      </div>

      <Modal
        open={open}
        onOk={showModal}
        onCancel={hideModal}
        closeIcon={<img src={Close} alt="Close" />}
        footer={null}
      >
        <div className="direction-modal-container">
          <div className='direction-modal-title'>Directions</div>
          <div className='direction-modal-content'>
            <div className="assessment1-content direction-content">
              Have you ever changed your mind about what you're going to order at a restaurant? Your six guests are especially indecisive. What's more, they are adamant about changing the seating arrangement so that
            </div>
            <div className="assessment1-content direction-content">
              1. Each guest is seated next to a guest ordering their second favorite dish
            </div>
            <div className="assessment1-content direction-content2">
              2. No guest is sitting next to a guest eating their detested dish.
            </div>
            <div className="assessment1-content direction-content">
              You can forget about what you thought the guests liked and disliked when you read the specials.
            </div>
            <div className="assessment1-content direction-content">
              This exercise will change the guests' preferences and test your pattern recognition abilities on the fly.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Adaptive Module 2 Component
const AdaptiveModule2 = (props) => {
  console.log('🎯 AdaptiveModule2 props:', props);
  console.log('🎯 AdaptiveModule2 cardData:', props.cardData);
  
  return (
    <AssessmentWrapper 
      moduleType="2" 
      isAdaptive={true}
      onModuleComplete={props.onModuleComplete}
      renderAdaptiveContent={(contentProps) => <SeatingGuestsContent {...contentProps} dynamicCardData={props.cardData} />}
    />
  );
};

export default AdaptiveModule2;